<template>
<!-- Sync Logs Button -->
<v-tooltip bottom>
	<template v-slot:activator="{ props }">
		<v-btn
			icon
			color="info"
			size="small"
			rounded="lg"
			@click="showSyncLogs = true"
			class="logs-btn ml-2"
			v-bind="props"
		>
			<v-icon>mdi-text-box-search-outline</v-icon>
		</v-btn>
	</template>
	<span>See sync status</span>
</v-tooltip>

<v-dialog v-model="showSyncLogs" max-width="1200px" scrollable>
<v-card>
	<v-card-title class="d-flex align-center justify-space-between">
		<span>Sync Status & Logs</span>
		<v-tooltip bottom>
			<template v-slot:activator="{ props }">
				<v-btn
					icon
					:loading="loadingSyncLogs"
					@click="refreshSyncStatus"
					size="small"
					rounded="lg"
					color="grey lighten-1"
					v-bind="props"
				>
					<v-icon size="18">mdi-refresh</v-icon>
				</v-btn>
			</template>
			<span>Refresh logs</span>
		</v-tooltip>
	</v-card-title>

	<v-card-text>
		<!-- Sync logs content will go here -->
		 	<!-- Current Sync Status -->
		<div class="sync-logs-section">
			<h4 class="mb-2">Recent Sync Details</h4>
			<div class="logs-container">
				<div
					v-if="syncLogs.length === 0"
					class="text-body-2 text-medium-emphasis pa-4 text-center"
				>
					No sync records available
				</div>
				<div v-else>
					<div
						v-for="(syncRecord, index) in syncLogs"
						:key="syncRecord.sync_id || index"
						class="sync-record mb-4"
					>
						<!-- Sync Record Header -->
						<div class="sync-header p-3 mb-2" :class="getSyncRecordClass(syncRecord)">
							<div class="d-flex align-center justify-space-between">
								<div class="d-flex align-center flex-grow-1">
									<v-icon :color="getLogIconColor(syncRecord)" size="20" class="mr-2">
										{{ getLogIcon(syncRecord) }}
									</v-icon>
									<div class="flex-grow-1">
										<div class="d-flex align-center flex-wrap">
											<strong class="mr-2">{{ syncRecord.status }}</strong>
											<span v-if="syncRecord.api_type" class="text-body-2 mr-2">
												({{ syncRecord.api_type }})
											</span>
											<span v-if="getSyncProgress(syncRecord) !== null" class="text-body-2">
												{{ getSyncProgress(syncRecord) }}% complete
											</span>
										</div>
										<div v-if="syncRecord.current_entity && isSyncRunning(syncRecord)" class="mt-1">
											<span class="text-body-2 font-weight-medium">
												Currently processing: 
												<span class="current-entity-badge">{{ syncRecord.current_entity }}</span>
											</span>
										</div>
										<div v-if="syncRecord.total_entities" class="mt-1 text-body-2">
											{{ syncRecord.completed_entities }}/{{ syncRecord.total_entities }} entities
											<span v-if="syncRecord.failed_entities > 0" class="text-error ml-1">
												({{ syncRecord.failed_entities }} failed)
											</span>
										</div>
										<div v-if="syncRecord.entities_requested" class="mt-1 text-body-2">
											Requested: {{ syncRecord.entities_requested }}
											<span v-if="syncRecord.entities_skipped" class="text-medium-emphasis ml-1">
												(Skipped: {{ syncRecord.entities_skipped }})
											</span>
										</div>
									</div>
								</div>
								<div class="text-body-2 ml-4">
									<div class="mb-1">
										<div>
											<strong>Started:</strong> {{ formatLogTime(syncRecord.started_at) }}
											<span class="text-medium-emphasis ml-1">({{ formatRelativeTime(syncRecord.started_at) }})</span>
										</div>
										<div v-if="syncRecord.completed_at">
											<strong>Completed:</strong> {{ formatLogTime(syncRecord.completed_at) }}
											<span class="text-medium-emphasis ml-1">({{ formatRelativeTime(syncRecord.completed_at) }})</span>
										</div>
										<div v-else-if="isSyncRunning(syncRecord)" class="mt-1">
											<strong>Elapsed:</strong> {{ calculateElapsedTime(syncRecord.started_at) }}
										</div>
									</div>
									<div v-if="syncRecord.completed_at && syncRecord.started_at" class="mt-1">
										<strong>Duration:</strong> {{ calculateDuration(syncRecord.started_at, syncRecord.completed_at) }}
									</div>
								</div>
							</div>
							<div
								v-if="syncRecord.error_message"
								class="error-message pa-2 mb-2 text-error"
							>
								<v-icon size="16" class="mr-1">mdi-alert-circle</v-icon>
								{{ syncRecord.error_message }}
							</div>
						</div>

						<!-- Metadata Section -->
						<div v-if="syncRecord.sync_id || syncRecord.district_id || syncRecord.pid" class="metadata-section pa-3">
							<v-expansion-panels variant="accordion" class="mb-2">
								<v-expansion-panel>
									<v-expansion-panel-title>
										<v-icon size="16" class="mr-2">mdi-information-outline</v-icon>
										<span>Sync Metadata</span>
									</v-expansion-panel-title>
									<v-expansion-panel-text>
										<div class="metadata-grid">
											<div v-if="syncRecord.sync_id" class="metadata-item">
												<strong>Sync ID:</strong>
												<div class="d-flex align-center">
													<code class="sync-id-text">{{ syncRecord.sync_id }}</code>
													<v-tooltip bottom>
														<template v-slot:activator="{ props }">
															<v-btn
																icon
																size="x-small"
																variant="text"
																@click="copyToClipboard(syncRecord.sync_id)"
																v-bind="props"
																class="ml-1"
															>
																<v-icon size="14">mdi-content-copy</v-icon>
															</v-btn>
														</template>
														<span>Copy Sync ID</span>
													</v-tooltip>
												</div>
											</div>
											<div v-if="syncRecord.api_type" class="metadata-item">
												<strong>API Type:</strong> {{ syncRecord.api_type }}
											</div>
											<div v-if="syncRecord.district_id" class="metadata-item">
												<strong>District ID:</strong> {{ syncRecord.district_id }}
											</div>
											<div v-if="syncRecord.pid" class="metadata-item">
												<strong>Process ID:</strong> {{ syncRecord.pid }}
											</div>
										</div>
									</v-expansion-panel-text>
								</v-expansion-panel>
							</v-expansion-panels>
						</div>

						<!-- Entity Details -->
						<div
							v-if="syncRecord.entities && syncRecord.entities.length > 0"
							class="entity-details pa-3"
						>
							<h5 class="mb-3">Entity Details</h5>
							<div class="entity-grid">
								<div
									v-for="entity in syncRecord.entities"
									:key="entity.entity"
									class="entity-card pa-3 mb-2"
									:class="getEntityClass(entity)"
								>
									<div class="d-flex align-center justify-space-between mb-2">
										<div class="d-flex align-center">
											<v-icon :color="getLogIconColor(entity)" size="18" class="mr-2">
												{{ getLogIcon(entity) }}
											</v-icon>
											<strong>{{ entity.entity }}</strong>
											<v-chip
												v-if="syncRecord.current_entity === entity.entity && isSyncRunning(syncRecord)"
												size="x-small"
												color="info"
												class="ml-2"
											>
												Current
											</v-chip>
										</div>
										<span class="entity-status">{{ entity.status }}</span>
									</div>
									
									<!-- Records Stats -->
									<div class="entity-stats mb-2">
										<div class="d-flex justify-space-between align-center mb-1">
											<div class="text-body-2">
												<v-icon size="14" class="mr-1">mdi-download</v-icon>
												Fetched: <strong>{{ entity.records_fetched || 0 }}</strong> records
											</div>
											<div class="text-body-2">
												<v-icon size="14" class="mr-1">mdi-merge</v-icon>
												Merged: <strong>{{ entity.records_merged || 0 }}</strong> records
											</div>
										</div>
									</div>

									<!-- Fetch Phase -->
									<div v-if="entity.fetch_started_at || entity.fetch_completed_at" class="entity-phase mb-2">
										<div class="phase-header">
											<v-icon size="14" class="mr-1">mdi-download</v-icon>
											<strong class="text-body-2">Fetch Phase</strong>
										</div>
										<div class="phase-details text-body-2 ml-4">
											<div v-if="entity.fetch_started_at">
												Started: {{ formatLogTime(entity.fetch_started_at) }}
												<span class="text-medium-emphasis ml-1">({{ formatRelativeTime(entity.fetch_started_at) }})</span>
											</div>
											<div v-if="entity.fetch_completed_at">
												Completed: {{ formatLogTime(entity.fetch_completed_at) }}
												<span class="text-medium-emphasis ml-1">({{ formatRelativeTime(entity.fetch_completed_at) }})</span>
											</div>
											<div v-if="entity.fetch_started_at && entity.fetch_completed_at" class="mt-1">
												Duration: {{ calculateDuration(entity.fetch_started_at, entity.fetch_completed_at) }}
											</div>
											<div v-else-if="entity.fetch_started_at && !entity.fetch_completed_at" class="mt-1">
												Elapsed: {{ calculateElapsedTime(entity.fetch_started_at) }}
											</div>
										</div>
									</div>

									<!-- Merge Phase -->
									<div v-if="entity.merge_started_at || entity.merge_completed_at" class="entity-phase mb-2">
										<div class="phase-header">
											<v-icon size="14" class="mr-1">mdi-merge</v-icon>
											<strong class="text-body-2">Merge Phase</strong>
										</div>
										<div class="phase-details text-body-2 ml-4">
											<div v-if="entity.merge_started_at">
												Started: {{ formatLogTime(entity.merge_started_at) }}
												<span class="text-medium-emphasis ml-1">({{ formatRelativeTime(entity.merge_started_at) }})</span>
											</div>
											<div v-if="entity.merge_completed_at">
												Completed: {{ formatLogTime(entity.merge_completed_at) }}
												<span class="text-medium-emphasis ml-1">({{ formatRelativeTime(entity.merge_completed_at) }})</span>
											</div>
											<div v-if="entity.merge_started_at && entity.merge_completed_at" class="mt-1">
												Duration: {{ calculateDuration(entity.merge_started_at, entity.merge_completed_at) }}
											</div>
											<div v-else-if="entity.merge_started_at && !entity.merge_completed_at" class="mt-1">
												Elapsed: {{ calculateElapsedTime(entity.merge_started_at) }}
											</div>
										</div>
									</div>

									<!-- Error Message -->
									<div v-if="entity.error_message" class="text-error mt-2 pt-2 border-top">
										<v-icon size="14" class="mr-1">mdi-alert-circle</v-icon>
										<strong>Error:</strong> {{ entity.error_message }}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</v-card-text>

	<v-card-actions>
		<v-spacer />
		<v-btn color="grey lighten-1" @click="showSyncLogs = false">Close</v-btn>
	</v-card-actions>
</v-card>
</v-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useSyncStatus } from '@/composables/useSyncStatus';
import dayjs from "dayjs";
import timezone from "dayjs/esm/plugin/timezone";
import { useSchoolStore } from "@/stores/school";

dayjs.extend(timezone);

// Define props
interface Props {
	integration_type?: string;
}

const props = withDefaults(defineProps<Props>(), {
	integration_type: "1.1",
});

// Use the sync status composable
const { syncLogs, fetchSyncStatus } = useSyncStatus(props.integration_type);

// Get school store for timezone
const schoolStore = useSchoolStore();

// Local state for dialog
const showSyncLogs = ref(false);
const loadingSyncLogs = ref(false);
const copySuccess = ref(false);

// Constants
const SYNC_STATUS_COMPLETED = "COMPLETED";
const SYNC_STATUS_IN_PROGRESS = "IN_PROGRESS";
const SYNC_STATUS_RUNNING = "RUNNING";

// Refresh function for the button in dialog header
const refreshSyncStatus = async () => {
	loadingSyncLogs.value = true;
	try {
		await fetchSyncStatus();
	} finally {
		loadingSyncLogs.value = false;
	}
};

// Copy to clipboard function
const copyToClipboard = async (text: string) => {
	try {
		await navigator.clipboard.writeText(text);
		copySuccess.value = true;
		setTimeout(() => {
			copySuccess.value = false;
		}, 2000);
	} catch (err) {
		console.error('Failed to copy text:', err);
	}
};

// Check if sync is running
const isSyncRunning = (syncRecord: any) => {
	const status = syncRecord.status?.toLowerCase();
	return status === 'running' || status === 'in_progress' || status === 'in-progress';
};

// Calculate sync progress percentage
const getSyncProgress = (syncRecord: any) => {
	if (!syncRecord.total_entities || syncRecord.total_entities === 0) {
		return null;
	}
	return Math.round((syncRecord.completed_entities / syncRecord.total_entities) * 100);
};

// Calculate duration between two timestamps
const calculateDuration = (startTime: string, endTime: string): string => {
	if (!startTime || !endTime) return 'N/A';
	
	const start = new Date(startTime);
	const end = new Date(endTime);
	const diffMs = end.getTime() - start.getTime();
	
	return formatDuration(diffMs);
};

// Calculate elapsed time from a start timestamp to now
const calculateElapsedTime = (startTime: string): string => {
	if (!startTime) return 'N/A';
	
	// Get school timezone
	const schoolTimezone = schoolStore.school?.timezone || 'America/New_York';
	
	// Parse timestamp as if it's in school timezone (API returns it in school timezone)
	const start = dayjs.tz(startTime, schoolTimezone);
	// Get current time in school timezone
	const now = dayjs().tz(schoolTimezone);
	const diffMs = now.valueOf() - start.valueOf();
	
	return formatDuration(diffMs);
};

// Format duration in milliseconds to human-readable string
const formatDuration = (ms: number): string => {
	if (ms < 0) return '0s';
	
	const seconds = Math.floor(ms / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);
	
	if (days > 0) {
		return `${days}d ${hours % 24}h ${minutes % 60}m`;
	} else if (hours > 0) {
		return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
	} else if (minutes > 0) {
		return `${minutes}m ${seconds % 60}s`;
	} else {
		return `${seconds}s`;
	}
};

// Format relative time (e.g., "5 minutes ago")
const formatRelativeTime = (timestamp: string): string => {
	if (!timestamp) return 'N/A';
	
	// Get school timezone
	const schoolTimezone = schoolStore.school?.timezone || 'America/New_York';
	
	// Parse timestamp as if it's in school timezone (API returns it in school timezone)
	const time = dayjs.tz(timestamp, schoolTimezone);
	// Get current time in school timezone
	const now = dayjs().tz(schoolTimezone);
	
	const diffMs = now.valueOf() - time.valueOf();
	const diffSeconds = Math.floor(diffMs / 1000);
	const diffMinutes = Math.floor(diffSeconds / 60);
	const diffHours = Math.floor(diffMinutes / 60);
	const diffDays = Math.floor(diffHours / 24);
	
	if (diffSeconds < 60) {
		return 'just now';
	} else if (diffMinutes < 60) {
		return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
	} else if (diffHours < 24) {
		return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
	} else if (diffDays < 7) {
		return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
	} else {
		return formatLogTime(timestamp);
	}
};

const getLogEntryClass = (level: string) => {
	switch (level) {
		case "error":
			return "log-error";
		case "warning":
			return "log-warning";
		case "info":
			return "log-info";
		default:
			return "log-default";
	}
};

const getLogIconColor = (log: any) => {
	const status = log.status?.toLowerCase();
	if (status === SYNC_STATUS_COMPLETED.toLowerCase()) {
		return "success";
	} else if (status === "error" || log.error_message) {
		return "error";
	} else if (status === "running" || status === "in_progress" || status === "in-progress") {
		return "info";
	} else if (status === "pending") {
		return "grey";
	} else {
		return "primary";
	}
};

const getLogIcon = (log: any) => {
	const status = log.status?.toLowerCase();
	if (status === SYNC_STATUS_COMPLETED.toLowerCase()) {
		return "mdi-check-circle";
	} else if (status === "error" || log.error_message) {
		return "mdi-alert-circle";
	} else if (status === "running" || status === "in_progress" || status === "in-progress") {
		return "mdi-loading mdi-spin";
	} else if (status === "pending") {
		return "mdi-clock-outline";
	} else {
		return "mdi-circle-small";
	}
};

const formatLogTime = (timestamp: string) => {
	return new Date(timestamp).toLocaleString();
};

const getSyncRecordClass = (syncRecord: any) => {
	const status = syncRecord.status?.toLowerCase();
	if (status === SYNC_STATUS_COMPLETED.toLowerCase()) {
		return "sync-completed";
	} else if (status === "error" || syncRecord.error_message) {
		return "sync-error";
	} else if (status === "running" || status === "in_progress" || status === "in-progress") {
		return "sync-running";
	} else if (status === "pending") {
		return "sync-pending";
	} else {
		return "sync-default";
	}
};

const getEntityClass = (entity: any) => {
	const status = entity.status?.toLowerCase();
	if (status === SYNC_STATUS_COMPLETED.toLowerCase()) {
		return "entity-completed";
	} else if (status === "error" || entity.error_message) {
		return "entity-error";
	} else if (status === "running" || status === "in_progress" || status === "in-progress") {
		return "entity-running";
	} else if (status === "pending") {
		return "entity-pending";
	} else {
		return "entity-default";
	}
};



onMounted(async () => {
	// Fetch initial sync status and logs
	await fetchSyncStatus();
});
</script>

<style scoped>
.oneroster-table-container {
	width: 100%;
}
.oneroster-table {
	table-layout: fixed;
	width: 100%;
}
.table-body-scroll {
	height: 50vh;
	max-height: 50vh;
	overflow-y: auto;
}
.header-cell {
	background: #e5e4db;
	color: #222e3a;
	font-weight: 600;
	font-size: 1rem;
	padding: 3px 3px 3px 3px;
	border-bottom: 2px solid #e3e7ee;
	letter-spacing: 0.04em;
	text-align: left;
	vertical-align: middle;
}
.actions-header {
	text-align: center;
	width: 100px;
}
.reload-btn {
	margin-left: 8px;
	min-width: 32px;
	height: 32px;
	padding: 0;
}
.mapping-btn {
	margin-left: 8px;
}
.logs-btn {
	min-width: 32px;
	height: 32px;
	padding: 0;
}
.sync-progress {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	border-radius: 0 0 4px 4px;
}
.logs-container {
	max-height: 600px;
	overflow-y: auto;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	padding: 8px;
}
.log-entry {
	border-bottom: 1px solid #f5f5f5;
}
.log-entry:last-child {
	border-bottom: none;
}
.log-error {
	background-color: #ffebee;
	border-left: 4px solid #f44336;
}
.log-warning {
	background-color: #fff8e1;
	border-left: 4px solid #ff9800;
}
.log-info {
	background-color: #e3f2fd;
	border-left: 4px solid #2196f3;
}
.log-default {
	background-color: #fafafa;
	border-left: 4px solid #9e9e9e;
}
.sync-status-section {
	border-bottom: 1px solid #e0e0e0;
}
.config-display {
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	padding: 12px;
}
.config-display table {
	width: 100%;
}
.config-display td {
	padding: 8px;
}
.config-display td:first-child {
	width: 150px;
}

/* Sync Record Styles */
.sync-record {
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	overflow: hidden;
}
.sync-header {
	border-radius: 8px 8px 0 0;
}
.sync-completed {
	background-color: #e8f5e8;
	border-left: 4px solid #4caf50;
}
.sync-error {
	background-color: #ffebee;
	border-left: 4px solid #f44336;
}
.sync-running {
	background-color: #e3f2fd;
	border-left: 4px solid #2196f3;
}
.sync-default {
	background-color: #fafafa;
	border-left: 4px solid #9e9e9e;
}
.error-message {
	background-color: #ffebee;
	border-left: 3px solid #f44336;
	border-radius: 4px;
}

/* Entity Details Styles */
.entity-details {
	padding: 0 16px 16px 16px;
}
.entity-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 12px;
}
.entity-card {
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	background-color: #fafafa;
}
.entity-completed {
	background-color: #e8f5e8;
	border-color: #4caf50;
}
.entity-error {
	background-color: #ffebee;
	border-color: #f44336;
}
.entity-running {
	background-color: #e3f2fd;
	border-color: #2196f3;
}
.entity-default {
	background-color: #fafafa;
	border-color: #e0e0e0;
}
.entity-status {
	font-size: 0.875rem;
	font-weight: 500;
}
.entity-stats {
	color: #666;
}

/* Metadata Section Styles */
.metadata-section {
	background-color: #fafafa;
	border-top: 1px solid #e0e0e0;
	border-bottom: 1px solid #e0e0e0;
}

.metadata-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
	gap: 16px;
}

.metadata-item {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.sync-id-text {
	font-family: monospace;
	font-size: 0.875rem;
	background-color: #f5f5f5;
	padding: 4px 8px;
	border-radius: 4px;
	word-break: break-all;
}

/* Current Entity Badge */
.current-entity-badge {
	display: inline-block;
	background-color: #2196f3;
	color: white;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 0.75rem;
	font-weight: 600;
}

/* Entity Phase Styles */
.entity-phase {
	margin-top: 8px;
	padding-top: 8px;
	border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.phase-header {
	display: flex;
	align-items: center;
	margin-bottom: 4px;
}

.phase-details {
	margin-left: 20px;
}

.phase-details > div {
	margin-bottom: 2px;
}

/* Sync Pending Styles */
.sync-pending {
	background-color: #f5f5f5;
	border-left: 4px solid #9e9e9e;
}

.entity-pending {
	background-color: #f5f5f5;
	border-color: #9e9e9e;
}

/* Enhanced Entity Card */
.entity-card {
	transition: box-shadow 0.2s;
}

.entity-card:hover {
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
